import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Star, ExternalLink } from 'lucide-react';
import { apiGet } from '../lib/api';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [footerLinks, setFooterLinks] = useState(null);
  const [hasStaticFooter, setHasStaticFooter] = useState(false);

  useEffect(() => {
    if (document.querySelector('[data-static-footer="true"]')) {
      setHasStaticFooter(true);
    }
  }, []);

  useEffect(() => {
    if (!hasStaticFooter) {
      fetchFooterLinks();
    }
  }, [hasStaticFooter]);

  const fetchFooterLinks = async () => {
    const data = await apiGet('/api/cms/footer-links');
    if (data && data.success && data.groups) {
      setFooterLinks(data.groups);
    } else {
      // Use fallback if API returns no data or error
      setFooterLinks(null);
    }
  };

  // Static fallback links - used when CMS has no data
  // URL Standard: WITH trailing slash (matches sitemap and canonical URLs)
  const staticLinks = {
    best_airports: {
      title: 'Best Airports',
      links: [
        { label: 'Istanbul Airport Transfer', url: '/istanbul-airport-transfer/' },
        { label: 'Antalya Airport Transfer', url: '/antalya-airport-transfer/' },
        { label: 'Dalaman Airport Transfer', url: '/dalaman-airport-transfer/' },
        { label: 'Bodrum Airport Transfer', url: '/bodrum-airport-transfer/' },
      ]
    },
    services: {
      title: 'Services',
      links: [
        { label: 'VIP Transfer Services', url: '/turkey-vip-transfer-services/' },
        { label: 'Medical Tourism Transfers', url: '/medical-tourism-turkey-vip-transfers/' },
        { label: 'Cancellation & Refund Policy', url: '/cancellation-refund-policy/' },
      ]
    },
    company: {
      title: 'Company',
      links: [
        { label: 'About Us', url: '/about/' },
        { label: 'FAQ', url: '/faq/' },
        { label: 'Terms of Service', url: '/terms-of-service/' },
        { label: 'Privacy Policy', url: '/privacy-policy/' },
        { label: 'Cookie Policy', url: '/cookie-policy/' },
      ]
    },
    resources: {
      title: 'Resources',
      links: [
        { label: 'Blog', url: '/blog/' },
        { label: 'Contact', url: '/contact/' },
      ]
    }
  };

  // Merge CMS links with static - CMS takes priority, static fills gaps
  const getGroupLinks = (groupKey) => {
    const cmsGroup = footerLinks?.[groupKey];
    const staticGroup = staticLinks[groupKey];
    
    // If CMS has links for this group, use them
    if (cmsGroup && cmsGroup.links && cmsGroup.links.length > 0) {
      return {
        title: cmsGroup.title || staticGroup.title,
        links: cmsGroup.links
      };
    }
    
    // Otherwise use static links
    return staticGroup;
  };

  // Get all link groups
  const bestAirports = getGroupLinks('best_airports');
  const services = getGroupLinks('services');
  const company = getGroupLinks('company');
  const resources = getGroupLinks('resources');

  // If static pre-rendered footer exists in DOM, skip React footer to avoid duplicate
  if (hasStaticFooter) return null;

  return (
    <footer id="contact" className="bg-dark-elevated border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            {/* Logo */}
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_27fa83fe-13fc-43b1-921c-bb11a32684e7/artifacts/gfuh061x_go-turkey-transfer-footer.png" 
                alt="GoTurkeyTransfer" 
                className="h-12 md:h-16 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm mb-6">
              Premium airport transfer services across Turkey. Licensed drivers, fixed prices, 24/7 availability.
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-4 mb-4">
              <a
                href="https://www.facebook.com/Goturkeytransfercom"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/goturkeytransfer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@goturkeytransfer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="TikTok"
                data-testid="footer-tiktok"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href="https://www.tripadvisor.com.tr/Attraction_Review-g293974-d34229539-Reviews-Go_Turkey_Transfer-Istanbul.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="TripAdvisor"
                data-testid="footer-tripadvisor"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a5.997 5.997 0 0 0 4.04 10.43 5.976 5.976 0 0 0 4.075-1.6L12 19.705l1.922-2.09a5.972 5.972 0 0 0 4.072 1.598 5.997 5.997 0 0 0 4.04-10.43L24 6.647h-4.35a13.573 13.573 0 0 0-7.644-2.352zM6.003 17.213a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm12.006 0a4 4 0 1 1 0-8 4 4 0 0 1 0 8zM6.003 11.213a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm12.006 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                </svg>
              </a>
              <a
                href="https://share.google/0v3PLN2IZaxXa6S1o"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Google Business"
                data-testid="footer-google-business"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </a>
            </div>
            {/* Payment Methods */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">We accept:</span>
              <div className="flex items-center gap-2 text-foreground/60">
                <span className="text-xs font-semibold">PayPal</span>
                <span className="text-xs">|</span>
                <span className="text-xs font-semibold">Cards</span>
              </div>
            </div>

            {/* Google Play Badge */}
            <a
              href="https://play.google.com/store/apps/details?id=com.goturkeytransfer.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4"
              aria-label="Get it on Google Play"
              data-testid="footer-google-play"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-10 w-auto"
                loading="lazy"
              />
            </a>
          </div>

          {/* Best Airports - using <a> tags for SEO crawlability */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4" data-testid="footer-airports-title">
              {bestAirports.title}
            </h4>
            <ul className="space-y-3">
              {bestAirports.links.map((link, index) => (
                <li key={`airport-${link.url}-${index}`}>
                  <a
                    href={link.url.startsWith('/') ? link.url : `/${link.url}`}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                    data-testid={`footer-airport-${index}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - using <a> tags for SEO crawlability */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4" data-testid="footer-services-title">
              {services.title}
            </h4>
            <ul className="space-y-3">
              {services.links.map((link, index) => (
                <li key={`service-${link.url}-${index}`}>
                  <a
                    href={link.url.startsWith('/') ? link.url : `/${link.url}`}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                    data-testid={`footer-service-${index}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company - using <a> tags for SEO crawlability */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4" data-testid="footer-company-title">
              {company.title}
            </h4>
            <ul className="space-y-3">
              {company.links.map((link, index) => (
                <li key={`company-${link.url}-${index}`}>
                  <a
                    href={link.url.startsWith('/') ? link.url : `/${link.url}`}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                    data-testid={`footer-company-${index}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              {/* Blog link - always visible */}
              <li>
                <a
                  href="/blog/"
                  className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  data-testid="footer-blog"
                >
                  Blog
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+905352385797"
                  className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="h-4 w-4 text-primary" />
                  <span className="text-sm">+90 535 238 5797</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@goturkeytransfer.com"
                  className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="h-4 w-4 text-primary" />
                  <span className="text-sm">info@goturkeytransfer.com</span>
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/905352385797"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="h-4 w-4 text-primary" />
                  <span className="text-sm">WhatsApp</span>
                </a>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-contact-page"
                >
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="text-sm">Contact Us</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* As Seen On / Featured Section */}
        <div className="mt-10 pt-8 border-t border-border">
          <div className="text-center mb-6">
            <span className="text-sm text-muted-foreground uppercase tracking-wider">As Seen On</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {/* Google Reviews */}
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJy_493HvDLWcRdiQR-87vf-k"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-background/50 rounded-lg hover:bg-background/80 transition-colors group"
              data-testid="footer-google-reviews"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-medium text-foreground">4.9</span>
                <span className="text-xs text-muted-foreground">Google</span>
              </div>
            </a>

            {/* TripAdvisor */}
            <a
              href="https://www.tripadvisor.com.tr/Attraction_Review-g293974-d34229539-Reviews-Go_Turkey_Transfer-Istanbul.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-background/50 rounded-lg hover:bg-background/80 transition-colors group"
              data-testid="footer-tripadvisor-badge"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#00AA6C">
                <path d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a5.997 5.997 0 0 0 4.04 10.43 5.976 5.976 0 0 0 4.075-1.6L12 19.705l1.922-2.09a5.972 5.972 0 0 0 4.072 1.598 5.997 5.997 0 0 0 4.04-10.43L24 6.647h-4.35a13.573 13.573 0 0 0-7.644-2.352zM6.003 17.213a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm12.006 0a4 4 0 1 1 0-8 4 4 0 0 1 0 8zM6.003 11.213a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm12.006 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
              </svg>
              <span className="text-sm font-medium text-foreground">TripAdvisor</span>
            </a>

            {/* Kayak */}
            <a
              href="https://www.kayak.co.uk/Istanbul.3430.guide"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-background/50 rounded-lg hover:bg-background/80 transition-colors group"
              data-testid="footer-kayak-badge"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#FF690F">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="text-sm font-medium text-foreground">KAYAK Guide</span>
            </a>
          </div>
        </div>

        {/* Company Info & Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-border">
          {/* Company Registration Info */}
          <div className="mb-6 p-4 bg-background/30 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-foreground font-semibold">VIPGO TECH LTD</p>
                <p className="text-muted-foreground text-xs mt-1">Company Number: 17039074</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-muted-foreground text-xs">
                  <span className="text-foreground/80">Registered Office:</span> Unit 501 Leroy House, 434–436 Essex Road, London, United Kingdom, N1 3FY
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} GoTurkeyTransfer. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="/terms-of-service/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/privacy-policy/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/cancellation-refund-policy/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Refund Policy
              </a>
            </div>
          </div>
          <p className="text-xs text-muted-foreground/60 mt-4">
            VIPGO TECH LTD is registered in England and Wales. Subject to UK laws.
          </p>
          
          {/* Design Credit */}
          <div className="mt-6 pt-4 border-t border-border/50 flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground/60">Designed by</span>
            <a
              href="https://www.psdagency.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors group"
              data-testid="footer-psd-credit"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_deploy-doctor-32/artifacts/hlmemxg2_psdagency%20%281%29.webp" 
                alt="PSD Agency" 
                className="h-4 w-auto opacity-60 group-hover:opacity-100 transition-opacity"
              />
              <span className="font-medium">PSD Agency</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
